import { useEffect } from "react"
import {
  OrderFragment,
  OrderStatus,
  OrderType,
  useOrderQuery
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import {
  popSignupActivationContext,
  trackSignupActivateEvents
} from "../plausible/useSignupEventTracking"
import { ORDER_FULFILLED_EVENT } from "../utils/dispatcher"
import { dispatchOrderEvent } from "./utils/order-events"

export type OnOrderFulfilledCallback = (order: OrderFragment) => void

export type UseOnOrderFullfiledProps = {
  order?: OrderFragment | null
  onOrderFulfilled?: OnOrderFulfilledCallback
}

export const useOnOrderFullfiled = ({
  order,
  onOrderFulfilled
}: UseOnOrderFullfiledProps) => {
  const { me } = usePathwrightContext()
  const orderFullfilled =
    !!order && order.order_status === OrderStatus.Fulfilled

  useEffect(() => {
    if (orderFullfilled) {
      onOrderFulfilled && onOrderFulfilled(order)
      dispatchOrderEvent(ORDER_FULFILLED_EVENT, { user: me, order })
    }
  }, [orderFullfilled])
}

export function usePollForOrderFulfillment(
  orderId?: number | null,
  options?: Partial<Parameters<typeof useOrderQuery>[0]>
) {
  const orderQuery = useOrderQuery({
    skip: !orderId,
    pollInterval: 3000,
    variables: {
      id: orderId!
    },
    ...options
  })

  const orderStatus = orderQuery.data?.order?.order_status
  const orderFulfilled = orderStatus === OrderStatus.Fulfilled

  // Stop polling when the order reaches a final status.
  useEffect(() => {
    switch (orderStatus) {
      case OrderStatus.Fulfilled:
      case OrderStatus.FulfillmentFailed:
      case OrderStatus.PaymentFailed:
      case OrderStatus.Voided:
        orderQuery.stopPolling()
        break
    }
  }, [orderStatus])

  return [orderFulfilled, orderQuery] as const
}

export default function useOrderPollingQuery(orderId: number) {
  const { me } = usePathwrightContext()

  const [orderFulfilled, orderQuery] = usePollForOrderFulfillment(orderId)

  useEffect(() => {
    if (orderFulfilled) {
      const order = orderQuery.data?.order
      dispatchOrderEvent(ORDER_FULFILLED_EVENT, { user: me, order })

      if (order?.order_type === OrderType.PlatformSubscription) {
        const activationContext = popSignupActivationContext(order.id)
        const spaceId = order.school_id || activationContext?.spaceId

        if (activationContext && spaceId) {
          trackSignupActivateEvents({
            spaceId,
            createdDate:
              activationContext.createdDate || new Date().toISOString(),
            packageKey: activationContext.packageKey,
            type: activationContext.type,
            userId:
              typeof me?.id === "number" ? me.id : activationContext.userId
          })
        }
      }
    }
  }, [orderFulfilled, me?.id])

  return orderQuery
}
